'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import BrandLogo from '@/components/ui/BrandLogo';

const navLinks = [
  { label: 'Destinations', href: '/destinations', icon: '🗺️' },
  { label: 'Packages', href: '/packages', icon: '📦' },
  { label: 'Hotels', href: '/hotels', icon: '🏨' },
  { label: 'Guides', href: '/guides', icon: '🧭' },
  { label: 'Vehicles', href: '/vehicles', icon: '🚗' },
  { label: 'Blog', href: '/blog', icon: '📝' },
];

export default function NotFound() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(160deg, #0d2347 0%, #1B3A6B 45%, #0f2a50 100%)' }}
    >
      {/* Top bar */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-white/5">
        <BrandLogo size={40} showTagline={false} />
        <Link
          href="/home"
          className="text-xs font-medium px-4 py-2 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-all"
        >
          ← Back to Home
        </Link>
      </header>
      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div
          className="w-full max-w-2xl mx-auto text-center"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          {/* Mountain SVG illustration */}
          <div className="flex justify-center mb-8" aria-hidden="true">
            <svg
              width="260"
              height="140"
              viewBox="0 0 260 140"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Background mountains */}
              <path
                d="M0 130 L50 55 L90 90 L130 30 L170 75 L210 40 L260 130 Z"
                fill="url(#mtGrad1)"
                opacity="0.18"
              />
              {/* Mid mountains */}
              <path
                d="M20 130 L65 60 L100 95 L140 35 L180 80 L220 45 L260 130 Z"
                fill="url(#mtGrad2)"
                opacity="0.28"
              />
              {/* Foreground peak */}
              <path
                d="M60 130 L130 18 L200 130 Z"
                fill="url(#mtGrad3)"
                opacity="0.55"
              />
              {/* Snow cap */}
              <path
                d="M130 18 L115 55 L130 48 L145 55 Z"
                fill="#F5F0E8"
                opacity="0.85"
              />
              {/* Sun / moon */}
              <circle cx="210" cy="28" r="14" fill="url(#sunGrad)" opacity="0.7" />
              {/* Horizon line */}
              <line x1="0" y1="130" x2="260" y2="130" stroke="#1ABCD4" strokeWidth="1.5" opacity="0.3" />
              {/* Stars */}
              <circle cx="40" cy="22" r="1.5" fill="#F5F0E8" opacity="0.6" />
              <circle cx="75" cy="12" r="1" fill="#F5F0E8" opacity="0.5" />
              <circle cx="185" cy="18" r="1.5" fill="#F5F0E8" opacity="0.6" />
              <circle cx="240" cy="30" r="1" fill="#F5F0E8" opacity="0.4" />
              <defs>
                <linearGradient id="mtGrad1" x1="130" y1="30" x2="130" y2="130" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#2E5FA3" />
                  <stop offset="1" stopColor="#1B3A6B" />
                </linearGradient>
                <linearGradient id="mtGrad2" x1="130" y1="35" x2="130" y2="130" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#1ABCD4" stopOpacity="0.4" />
                  <stop offset="1" stopColor="#1B3A6B" />
                </linearGradient>
                <linearGradient id="mtGrad3" x1="130" y1="18" x2="130" y2="130" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#C8960A" stopOpacity="0.9" />
                  <stop offset="1" stopColor="#1B3A6B" />
                </linearGradient>
                <radialGradient id="sunGrad" cx="50%" cy="50%" r="50%">
                  <stop stopColor="#F5C030" />
                  <stop offset="1" stopColor="#E8621A" />
                </radialGradient>
              </defs>
            </svg>
          </div>

          {/* 404 number */}
          <div
            className="font-serif font-bold leading-none mb-3 select-none"
            style={{
              fontSize: 'clamp(5rem, 18vw, 9rem)',
              background: 'linear-gradient(135deg, #C8960A, #E0AA18, #F5C030)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            404
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-white/20" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-white/30">
              Trail Not Found
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-white/20" />
          </div>

          <h1 className="text-white font-bold text-2xl sm:text-3xl mb-3 font-serif">
            Lost in the Himalayas?
          </h1>
          <p className="text-white/50 text-sm sm:text-base mb-10 max-w-md mx-auto leading-relaxed">
            The page you&apos;re looking for has wandered off the trail. Let&apos;s guide you back to exploring Nepal&apos;s wonders.
          </p>

          {/* Primary actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
            <Link
              href="/home"
              className="btn-gold px-8 py-3 rounded-full text-sm font-semibold inline-block"
            >
              🏔️ Return to Base Camp
            </Link>
            <Link
              href="/search"
              className="btn-outline-gold px-8 py-3 rounded-full text-sm font-semibold inline-block"
            >
              🔍 Search Nepal
            </Link>
          </div>

          {/* Quick nav grid */}
          <div className="border-t border-white/5 pt-8">
            <p className="text-xs text-white/30 uppercase tracking-widest mb-4 font-medium">
              Popular Destinations
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 max-w-lg mx-auto">
              {navLinks?.map(link => (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border border-white/5 hover:border-gold/30 hover:bg-white/5 transition-all group"
                >
                  <span className="text-xl group-hover:scale-110 transition-transform">{link?.icon}</span>
                  <span className="text-xs text-white/40 group-hover:text-gold transition-colors font-medium">
                    {link?.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      {/* Footer strip */}
      <footer className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
        <span className="text-xs text-white/20">© 2026 YatraNest Nepal</span>
        <Link href="/home" className="text-xs text-white/30 hover:text-gold transition-colors">
          Your Journey Begins Here ✦
        </Link>
      </footer>
    </div>
  );
}